import { isNativeApp } from './nativeApp';

/**
 * Storage abstraction for OAuth-flow-in-progress markers: 'oauth_state',
 * 'oauth_provider', 'oauth_pkce_verifier', 'linking_token', 'linking_provider',
 * 'delete_account_pending', 'delete_account_provider'.
 *
 * This is deliberately separate from authService.js's getAuthStorage(), which
 * governs the persisted auth session (token/user) — a different concern with
 * a different lifetime.
 *
 * Native app (Capacitor): localStorage. This is what actually makes the OAuth
 * flow survive a native "cold start": if the OS kills the app process while
 * the user is completing the flow in the system/in-app browser, Capacitor
 * recreates the WebView/JS context from scratch when the deep link relaunches
 * the app. sessionStorage does NOT survive that (it is tied to the JS
 * session/global object, which is gone with the killed process) — but
 * localStorage DOES, because the WebView persists it to disk independent of
 * the process lifecycle. Keeping oauth_state/oauth_pkce_verifier/... in
 * localStorage on native means they are still there when the deep link
 * relaunches the app, so the CSRF echo-check in OAuthCallback.js can — and
 * must — stay strict (no client-side relaxation) on native, exactly as on web.
 *
 * Web (default): sessionStorage — unchanged from before native support was
 * added.
 *
 * @returns {Storage}
 */
const getFlowStorage = () => (isNativeApp() ? window.localStorage : window.sessionStorage);

/** Every key ever written into flow storage — used by clearAllFlowItems(). */
export const FLOW_STORAGE_KEYS = [
  'oauth_state',
  'oauth_provider',
  'oauth_pkce_verifier',
  'linking_token',
  'linking_provider',
  'delete_account_pending',
  'delete_account_provider'
];

/**
 * Reads a flow-marker value from the platform-appropriate storage.
 * @param {string} key
 * @returns {string|null}
 */
export const getFlowItem = (key) => getFlowStorage().getItem(key);

/**
 * Writes a flow-marker value into the platform-appropriate storage.
 * @param {string} key
 * @param {string} value
 */
export const setFlowItem = (key, value) => getFlowStorage().setItem(key, value);

/**
 * Removes a flow-marker key from BOTH sessionStorage and localStorage,
 * regardless of the current platform. Defensive: guarantees no stale value
 * can survive in whichever storage isn't the "active" one for the current
 * platform (e.g. a leftover from before this abstraction existed, a
 * platform-detection edge case, or a value written while isNativeApp() briefly
 * disagreed with itself). Wrapped in try/catch since storage access can throw
 * in some Capacitor/privacy-mode environments (see InstallAppBanner.js).
 * @param {string} key
 */
export const removeFlowItem = (key) => {
  try {
    window.sessionStorage.removeItem(key);
  } catch (e) {
    // Storage unavailable — nothing more we can do here.
  }
  try {
    window.localStorage.removeItem(key);
  } catch (e) {
    // Storage unavailable — nothing more we can do here.
  }
};

/** Removes ALL known flow-marker keys from both storages. */
export const clearAllFlowItems = () => {
  FLOW_STORAGE_KEYS.forEach(removeFlowItem);
};
